import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../auth/useAuthStore'
import { fetchAllTransactions, fetchTransactionsForAccount } from '../api/trading'
import type { Transaction, TransactionKind } from '../api/types'

export interface TxFilters {
  start?: string
  end?: string
  symbol?: string
  kind?: TransactionKind
  limit?: number
}

export interface UseTransactionsResult {
  data: Transaction[] | undefined
  isLoading: boolean
  error: Error | null
  refetch: () => void
  isSyncing: boolean
}

const STALE_MS = 5 * 60 * 1000

export function useTransactions(filters: TxFilters = {}): UseTransactionsResult {
  const accountId = useAuthStore((s) => s.accountId)
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  const isAggregate = scopeAccountId === null && accounts.length > 1

  const query = useQuery<Transaction[], Error>({
    queryKey: ['tx', accountId, filters.start ?? null, filters.end ?? null],
    enabled: !!accountId && !isAggregate,
    staleTime: STALE_MS,
    queryFn: () => fetchAllTransactions({ start: filters.start, end: filters.end }),
  })

  const aggregateIds = useMemo(
    () => (isAggregate ? accounts.map((a) => a.accountId) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAggregate, accounts.map((a) => a.accountId).join(',')],
  )

  const aggregateQueries = useQueries({
    queries: aggregateIds.map((id) => ({
      queryKey: ['tx', id, filters.start ?? null, filters.end ?? null],
      queryFn: () => fetchTransactionsForAccount(id, { start: filters.start, end: filters.end }),
      staleTime: STALE_MS,
    })),
  })

  const aggregateData = useMemo<Transaction[] | undefined>(() => {
    if (!isAggregate) return undefined
    if (aggregateQueries.length === 0) return []
    if (aggregateQueries.some((q) => !q.data)) return undefined
    const merged: Transaction[] = []
    for (const q of aggregateQueries) {
      if (q.data) merged.push(...q.data)
    }
    return merged
  }, [
    isAggregate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    aggregateQueries.map((q) => q.dataUpdatedAt).join(','),
  ])

  const rawData = isAggregate ? aggregateData : query.data
  const filtered = rawData ? applyClientFilters(rawData, filters) : rawData

  const isLoading = isAggregate
    ? aggregateQueries.length === 0 || aggregateQueries.some((q) => q.isLoading)
    : query.isLoading
  const error = isAggregate
    ? (aggregateQueries.find((q) => q.error)?.error as Error | undefined) ?? null
    : query.error
  const isSyncing = isAggregate
    ? aggregateQueries.some((q) => q.isFetching)
    : query.isFetching

  return {
    data: filtered,
    isLoading,
    error: error ?? null,
    refetch: () => {
      if (isAggregate) {
        for (const q of aggregateQueries)
          q.refetch().catch((err) => console.warn('[transactions] refetch failed', err))
      } else {
        query.refetch().catch((err) => console.warn('[transactions] refetch failed', err))
      }
    },
    isSyncing,
  }
}

function applyClientFilters(rows: Transaction[], filters: TxFilters): Transaction[] {
  let out = rows
  if (filters.symbol) {
    out = out.filter((t) => t.symbol === filters.symbol)
  }
  if (filters.kind) {
    out = out.filter((t) => t.kind === filters.kind)
  }
  out = [...out].sort((a, b) => b.timestamp.localeCompare(a.timestamp))
  if (filters.limit && filters.limit > 0) {
    out = out.slice(0, filters.limit)
  }
  return out
}
